import { useCallback, useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { ALL_OPTION } from '@/common/constants';
import { DEFAULT_DATE_FORMAT, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';
import { useCurrentOrganization } from '@/modules/core/hooks';

import { useScheduledContributionRecordsFiltersStore, useScheduledContributionStore } from '../../stores';
const QUERY_KEY = 'GET_SCHEDULED_CONTRIBUTION_EXPORT';
const useScheduledContributionExportQuery = () => {
  const { observingEmployer, observingSubsidiary } = useCurrentOrganization();

  const calendarId = useScheduledContributionStore((state) => state.state.calendarId);
  const taxType = useScheduledContributionStore((state) => state.state.taxType);
  const startDate = useScheduledContributionStore((state) => state.state.startDate);

  const currentAccounts = useScheduledContributionRecordsFiltersStore((state) => state.state.currentAccounts);
  const searchString = useScheduledContributionRecordsFiltersStore((state) => state.state.searchString);
  const filteredAccounts = useMemo(() => {
    if (!currentAccounts || currentAccounts.includes(ALL_OPTION.value)) {
      return [];
    }
    return currentAccounts.filter((item) => item !== ALL_OPTION.value);
  }, [currentAccounts]);
  const fileName = useMemo(() => {
    const employerName = observingEmployer?.name || '';
    const subsidiaryName = observingSubsidiary?.name || '';
    let name = '';
    if (employerName) {
      name += `${employerName}_`;
    }
    if (subsidiaryName) {
      name += `${subsidiaryName}_`;
    }
    name += 'Scheduled_Contribution';
    if (startDate) {
      name += `_${startDate}`;
    }
    return `${name}.csv`.trim();
  }, [observingEmployer?.name, observingSubsidiary?.name, startDate]);
  const exportParams = useMemo(() => {
    const params = new URLSearchParams();

    if (calendarId) params.append('calendar_id', calendarId);
    if (taxType) params.append('tax_type', taxType);
    if (startDate) params.append('start_date', dayjs(startDate, DEFAULT_DATE_FORMAT).format(RAW_DATE_FORMAT));

    if (filteredAccounts.length > 0) {
      params.append('plan_codes', filteredAccounts.join(','));
    }
    if (searchString) params.append('name', searchString);

    return params;
  }, [calendarId, taxType, startDate, filteredAccounts, searchString]);

  const { isLoading, refetch } = useQuery(
    [QUERY_KEY, calendarId, startDate, filteredAccounts, searchString],
    () =>
      api.get(API_URL.SCHEDULED_CONTRIBUTION_SEARCH_CALENDAR_CSV_REPORT_BY_CALENDAR_ID(calendarId || ''), {
        params: exportParams,
      }),
    {
      retry: 0,
      enabled: false,
    }
  );
  const handleDownload = useCallback(async () => {
    const response = await refetch();
    if (response?.data?.data) {
      const url = window.URL.createObjectURL(new Blob([response.data.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    }
  }, [refetch, fileName]);
  return {
    isLoading,
    handleDownload,
    fileName,
  };
};

export default useScheduledContributionExportQuery;
